import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">该漫画不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 6);

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    time: `${Math.max(1, Math.floor((comic.chapters - i) / 3))}天前`,
  }));

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dandanmanhua.com' },
      { '@type': 'ListItem', position: 2, name: `${category?.name || '漫画'}`, item: `https://dandanmanhua.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `https://dandanmanhua.com/comic/${comic.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 耽耽漫画在线免费阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
  };

  return (
    <div className="min-h-screen pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
            {category?.name || '漫画'}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 whitespace-nowrap">{comic.title}</span>
        </nav>

        {/* Comic Detail Header */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
          <div className="w-full sm:w-[200px] md:w-[240px] flex-shrink-0 mx-auto md:mx-0">
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {comic.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <span className="text-sm text-foreground-500">作者：{comic.author}</span>
              <span className="text-sm text-foreground-500">|</span>
              <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-500' : 'text-foreground-600'}`}>
                {comic.status}
              </span>
              <span className="text-sm text-foreground-500">|</span>
              <span className="text-sm text-foreground-500">共 {comic.chapters} 话</span>
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500"></i>
                <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-eye-line"></i>
                <span>{(comic.views / 10000).toFixed(0)}万 阅读</span>
              </div>
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-5 max-w-2xl">
              {comic.description}
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-play-line"></i>
                开始阅读
              </button>
              <button
                type="button"
                className="px-5 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-bookmark-line"></i>
                加入书架
              </button>
            </div>
          </div>
        </div>

        {/* Chapter List */}
        <section className="mb-10" aria-labelledby="chapters-title">
          <div className="flex items-center justify-between mb-4">
            <h2 id="chapters-title" className="text-lg font-heading font-bold text-foreground-900">
              章节列表
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-xs text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                正序
              </button>
              <button
                type="button"
                className="text-xs text-primary-500 font-medium cursor-pointer whitespace-nowrap"
              >
                倒序
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {chapters.map((ch) => (
              <button
                key={ch.num}
                type="button"
                className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors duration-150 text-left cursor-pointer group"
              >
                <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors duration-150 line-clamp-1">
                  {ch.title}
                </span>
                <span className="text-xs text-foreground-400 whitespace-nowrap ml-2 flex-shrink-0">{ch.time}</span>
              </button>
            ))}
          </div>
          {comic.chapters > 20 && (
            <button
              type="button"
              className="mt-4 w-full py-2.5 rounded-lg bg-background-100 text-sm text-foreground-600 hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              查看全部 {comic.chapters} 话
            </button>
          )}
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section aria-labelledby="related-title">
            <h2 id="related-title" className="text-lg font-heading font-bold text-foreground-900 mb-4">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-2">
                    <img
                      src={rc.cover}
                      alt={rc.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                    {rc.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}